/**
 * Centralized motion system. Every component should pull durations and easings
 * from here so the entire app shares one motion language.
 *
 * Curve philosophy — restrained, professional, no overshoot:
 *
 *   entrance   — easeOutExpo. Quick start, soft settle. Things appearing.
 *   emphasized — OnePlus / iOS feel. Symmetric "expensive" curve for
 *                user-driven state changes (drawer slide, sidebar collapse).
 *   standard   — neutral easeInOut. Color / border / focus / hover shifts.
 *   exit       — quick out. Dismiss / fade.
 *
 * Durations are short by design. On desktop 60Hz+, anything past ~350ms reads
 * as sluggish. Drawers sit at 320ms; the rest is faster. Hovers are 120ms so
 * the cursor never outruns the feedback.
 *
 * Performance rules:
 *   • Animate only `transform` and `opacity` — they hit the compositor.
 *   • Never animate `box-shadow`, `backdrop-filter`, `width`, `height`,
 *     `top`, `left`, `padding`, `margin`. They retrigger layout / paint.
 *   • Avoid leaving `will-change` on idle elements (memory leak).
 *   • Every animation must respect `prefers-reduced-motion: reduce`.
 */

export const easing = {
  entrance:   'cubic-bezier(0.16, 1, 0.3, 1)',
  emphasized: 'cubic-bezier(0.32, 0.72, 0, 1)',
  standard:   'cubic-bezier(0.4, 0, 0.2, 1)',
  exit:       'cubic-bezier(0.4, 0, 1, 1)',
} as const;

export const duration = {
  micro: '120ms',  // hover, focus ring, color shift
  fast:  '180ms',  // button press, dropdown reveal, small popover
  base:  '240ms',  // modals, page mount
  slow:  '320ms',  // drawers, sidebar collapse, sheets
} as const;

/** [MOTION-A 2026-08-20] The same durations as numbers, for JS timers that must match a CSS
 *  exit (usePresence keeps an element mounted exactly this long). One source of truth. */
export const durationMs = {
  micro: 120,
  fast: 180,
  base: 240,
  slow: 320,
} as const;

export const transition = {
  /** Hover, focus, color / border tints. Composite — does not animate layout. */
  hover:
    `background-color ${duration.micro} ${easing.standard}, ` +
    `border-color ${duration.micro} ${easing.standard}, ` +
    `color ${duration.micro} ${easing.standard}, ` +
    `box-shadow ${duration.fast} ${easing.standard}, ` +
    `filter ${duration.micro} ${easing.standard}`,
  /** Button press: 1px translate down. */
  press: `transform ${duration.micro} ${easing.standard}`,
  /** Popover / dropdown enter. */
  popover: `transform ${duration.fast} ${easing.entrance}, opacity ${duration.fast} ${easing.standard}`,
  /** Modal scale+fade. */
  modal: `transform ${duration.base} ${easing.entrance}, opacity ${duration.fast} ${easing.standard}`,
  /** Full-height drawer slide. */
  drawer: `transform ${duration.slow} ${easing.emphasized}, opacity ${duration.fast} ${easing.standard}`,
} as const;

/**
 * [FLUID-1 2026-10-12, user] "Fluid" motion for everything that opens after a click — dropdown
 * lists, menus, calendars, popovers (Phase 1; sheets, dialogs and page landing follow). The thing
 * you clicked GROWS out of its button on a spring and the rows inside rise in one after another;
 * closing is quicker and mirrors it. Visible on purpose — the user asked for it — but still no
 * bounce: the spring is critically damped, the same `linear()` curve the dock pill glides on
 * (BottomNav SPRING_EASE). The CSS recipe lives in components/Fluid.tsx; these are the numbers.
 *
 * Timing (user, same day: "should not feel fast … should not be too slow that the app feels slow"):
 * the panel is readable after ~0.1 s (fade 200 ms, fast start) and settled by ~0.34 s (the spring is
 * front-loaded: ~75 % of the way at 0.14 s); every row inside has landed by ~0.45 s; closing takes
 * 0.18 s. Nothing waits on the motion — a pick fires at the click and the page updates at once.
 */
export const fluid = {
  spring: 'linear(0, 0.044, 0.143, 0.263, 0.383, 0.494, 0.592, 0.675, 0.743, 0.799, 0.844, 0.879, 0.907, 0.929, 0.946, 0.959, 0.969, 0.976, 0.982, 0.987, 1)',
  in: '340ms',      // the panel growing out of its button, on the spring
  fade: '200ms',    // the panel fading in — fast start, so it is readable almost at once
  out: '180ms',     // closing: quick, so the app never feels held up
  rise: '260ms',    // each row rising into place
  riseMs: 260,      // [FLUID-5 2026-10-12] the same, for the row wave (components/Fluid.tsx landRows)
  inMs: 340,
  outMs: 180,
  riseStartMs: 0,   // first row starts with the panel — [FLUID-8 2026-10-12] soft rows are readable from frame one (was 30)
  riseStepMs: 12,   // …and each next row this much later (was 16)
  riseItems: 10,    // rows after the 10th arrive together with it
  // [FLUID-2/3/4 2026-10-12] Side panels (Bin, Notifications, App Alerts), windows (dialogs / popups) and pages.
  // Panels were 380 / 240 ms; the speed pass on the real app measured App Alerts in place at ~480 ms and closed at
  // ~330 ms (render + spring tail) — over budget — so they run on the popovers' 340 ms and close in 220 ms.
  sheetIn: '340ms',
  sheetOut: '220ms',
  sheetOutMs: 220,
  dialogIn: '360ms',
  dialogOut: '180ms',
  dialogOutMs: 180,
  scrimIn: '220ms',
  scrimOut: '180ms',
} as const;

/**
 * [MOTION-A 2026-08-20] App-wide normalisation.
 *
 * 127 hand-written transitions across 37 files (`0.14s ease`, `0.2s ease`, `0.3s ease`, …)
 * were snapped onto these four durations and the `standard` curve, so hovers, focus rings and
 * colour shifts now feel identical in every tab instead of subtly different per screen. They
 * are written as LITERAL values (`120ms cubic-bezier(0.4, 0, 0.2, 1)`) rather than as imports
 * of the tokens above: adding an import to 37 files to express the same numbers is churn
 * without benefit. The literals and the tokens must stay equal — change one, change the other.
 *
 * Deliberately NOT normalised:
 *   • `transition: all` was left as `all` (only its timing changed). Narrowing it to explicit
 *     properties could silently drop a transition someone relies on — a Phase B/C job with
 *     per-component checking, not a mechanical sweep.
 *   • 9 overshoot curves (`cubic-bezier(0.34, 1.56, …)`, toggle knobs + calendar pop-ins) kept
 *     their bounce here — a look-and-feel decision for the user, not a cleanup.
 *     [MOTION-C 2026-08-20] DECIDED: the user chose to calm all 9. They now use `emphasized`,
 *     so nothing outside pmSingapore overshoots its resting point any more. The motion itself
 *     is unchanged — only the spring past the stop is gone.
 *   • components/pmSingapore/** and PMSingaporePage — the F1 board is its own design language.
 */

/** Media-query guard for reduced motion. Use inside styled-components blocks. */
export const reducedMotion = '@media (prefers-reduced-motion: reduce)';
